<template>
  <div class="onboarding-window max-w-4xl mx-auto w-full">
    <div class="onboarding-window-inside relative">
      <div class="flex flex-col h-full w-full absolute top-0 left-0 justify-stretch">
        <OnboardingHeader
          :currentScreenString="currentScreenString"
          :currentTitle="currentTitle"
          :currentScreen="currentScreen"
          :totalScreens="totalScreens"
          :nextDisabled="disableNext"
          @prevScreen="animationName = 'slide-left-fade'; currentScreen -= 1"
          @nextScreen="animationName = 'slide-right-fade'; currentScreen += 1"
        />
        <div class="flex flex-row flex-grow px-4 py-5 sm:p-6">
          <transition
            :name="animationName"
            mode="out-in"
          >
            <div
              v-if="currentScreen === 1"
              :key="`screen-1`"
              class="flex flex-col justify-center"
            >
              <p class="text-center text-gray-500 sm:px-5 mb-5">
                We want to share the quotes and analysis that would be most helpful to you.
                We would love to understand your needs on private student loans.
              </p>

              <span class="relative z-0 flex flex-row items-center justify-center">
                <button
                  type="button"
                  class="relative items-center px-4 py-2 rounded-l-md border text-sm
                  leading-5 font-medium focus:z-10 focus:outline-none focus:border-blue-300
                  focus:shadow-outline-blue transition ease-in-out duration-150
                  h-10 w-1/2 sm:w-1/3 md:w-1/4"
                  :class="selectedLoanTypes.includes('Graduate')
                    ? 'bg-green-500 border-green-500 text-white'
                    : 'bg-white text-gray-700 border-gray-300'"
                  @click="setLoanType('Graduate')"
                >
                  Graduate Loan
                </button>
                <button
                  type="button"
                  class="relative items-center px-4 py-2 rounded-r-md border text-sm
                  leading-5 font-medium focus:z-10 focus:outline-none focus:border-blue-300
                  focus:shadow-outline-blue transition ease-in-out duration-150
                  h-10 w-1/2 sm:w-1/3 md:w-1/4"
                  :class="selectedLoanTypes.includes('Undergraduate')
                    ? 'bg-green-500 border-green-500 text-white'
                    : 'bg-white text-gray-700 border-gray-300'"
                  @click="setLoanType('Undergraduate')"
                >
                  Undergraduate Loan
                </button>
              </span>
            </div>
            <div
              v-if="currentScreen === 2"
              :key="`screen-2`"
              class="flex flex-col justify-center"
            >
              <p class="text-center text-gray-500 sm:px-5 mb-5">
                A credit score is a number between 300-850 that depicts your creditworthiness.
                The higher the score, the better a borrower looks to potential lenders.
              </p>

              <div class="w-full sm:w-2/3 md:w-1/2 mx-auto">
                <Multiselect
                  v-model="$selectedCreditScores"
                  track-by="score"
                  label="score"
                  placeholder="Your credit score"
                  :multiple="true"
                  :disabled="loading"
                  :options="reversedCreditScores"
                  :searchable="false"
                  @input="handleFilterChange"
                ></Multiselect>
              </div>
            </div>
            <div
              v-if="currentScreen === 3"
              :key="`screen-3`"
              class="flex flex-col justify-center"
            >
              <transition name="fade-fast" mode="out-in">
                <div
                  v-if="!showCosignerCreditScores"
                  :key="`hide-cosigners`"
                >
                  <p class="text-center text-gray-500 sm:px-5 mb-5">
                    A co-signer is someone who is equally responsible for paying the loan.
                    A co-signer could be a parent, family member or friend. Almost always,
                    you will get a lower rate with a co-signer.
                  </p>
                  <div class="relative z-0 flex flex-row items-center justify-center mb-10">
                    <button
                      type="button"
                      class="relative items-center px-4 py-2 rounded-l-md border text-sm
                    leading-5 font-medium focus:z-10 focus:outline-none focus:border-blue-300
                    focus:shadow-outline-blue transition ease-in-out duration-150
                    h-10 w-1/2 sm:w-1/3 md:w-1/4"
                      :class="showCosignerCreditScores
                      ? 'bg-green-500 border-green-500 text-white'
                      : 'bg-white text-gray-700 border-gray-300'"
                      @click="toggleCosigners(true)"
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      class="relative items-center px-4 py-2 rounded-r-md border text-sm
                    leading-5 font-medium focus:z-10 focus:outline-none focus:border-blue-300
                    focus:shadow-outline-blue transition ease-in-out duration-150
                    h-10 w-1/2 sm:w-1/3 md:w-1/4"
                      :class="!showCosignerCreditScores
                      ? 'bg-green-500 border-green-500 text-white'
                      : 'bg-white text-gray-700 border-gray-300'"
                      @click="toggleCosigners(false)"
                    >
                      No
                    </button>
                  </div>
                </div>
                <div
                  v-if="showCosignerCreditScores"
                  class="text-center"
                  :key="`show-cosigners`"
                >
                  <p class="text-center text-gray-500 sm:px-5 mb-5">
                    If you are unsure about your co-signer’s credit score, you can select the
                    option, "I don’t know their credit score".
                  </p>
                  <div class="w-full sm:w-2/3 md:w-1/2 mx-auto mt-10">
                    <Multiselect
                      v-model="$selectedCosignerCreditScores"
                      track-by="score"
                      label="score"
                      placeholder="Your co-signers credit score"
                      :multiple="true"
                      :disabled="loading"
                      :options="reversedCosignerCreditScores"
                      :searchable="false"
                      @input="handleFilterChange"
                    ></Multiselect>
                  </div>
                  <button
                    class="text-green-400 text-xs mt-10 focus:outline-none
                    active:border-none"
                    @click="toggleCosigners(false)"
                  >
                    &#60;&#45; I don't have a co-signer
                  </button>
                </div>
              </transition>
            </div>
            <div
              v-if="currentScreen === 4"
              :key="`screen-4`"
              class="flex flex-col justify-center"
            >
              <p class="text-center text-gray-500 sm:px-5 mb-5">
                Fixed rates are constant for the whole duration of the loan. Variable rates
                change as the market conditions evolve but typically depend on a benchmark rate
                such as
                <Tooltip tooltip="London Interbank Offered Rate">
                  <span class="underline inline">LIBOR</span>.
                </Tooltip>
                To learn more,
                <a
                  href="https://leveredge.org/fixed-vs-variable-20-21"
                  class="text-green-500 underline"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  check out this resource.<svg
                    aria-hidden="true"
                    role="img"
                    viewBox="0 0 512 512"
                    width="12"
                    height="12"
                    class="inline mb-1 ml-1"
                  >
                    <path fill="currentColor" d="M440,256H424a8,8,0,0,0-8,8V464a16,16,0,0,1-16,16H48a16,16,0,0,1-16-16V112A16,16,0,0,1,48,96H248a8,8,0,0,0,8-8V72a8,8,0,0,0-8-8H48A48,48,0,0,0,0,112V464a48,48,0,0,0,48,48H400a48,48,0,0,0,48-48V264A8,8,0,0,0,440,256ZM500,0,364,.34a12,12,0,0,0-12,12v10a12,12,0,0,0,12,12L454,34l.7.71L131.51,357.86a12,12,0,0,0,0,17l5.66,5.66a12,12,0,0,0,17,0L477.29,57.34l.71.7-.34,90a12,12,0,0,0,12,12h10a12,12,0,0,0,12-12L512,12A12,12,0,0,0,500,0Z"></path>
                  </svg>
                </a>
              </p>

              <span class="relative z-0 flex flex-row items-center justify-center">
                <button
                  type="button"
                  class="relative items-center px-4 py-2 rounded-l-md border text-sm
                  leading-5 font-medium focus:z-10 focus:outline-none focus:border-blue-300
                  focus:shadow-outline-blue transition ease-in-out duration-150
                  h-10 w-1/2 sm:w-1/3 md:w-1/4"
                  @click="setRateType(rateTypes.find(rateType => rateType.id === 2))"
                  :class="selectedRateTypes.find(rateType => rateType.id === 2)
                      ? 'bg-green-500 border-green-500 text-white'
                      : 'bg-white text-gray-700 border-gray-300'"
                >
                  Variable
                </button>
                <button
                  type="button"
                  class="relative items-center px-4 py-2 rounded-r-md border text-sm
                  leading-5 font-medium focus:z-10 focus:outline-none focus:border-blue-300
                  focus:shadow-outline-blue transition ease-in-out duration-150
                  h-10 w-1/2 sm:w-1/3 md:w-1/4"
                  @click="setRateType(rateTypes.find(rateType => rateType.id === 1))"
                  :class="selectedRateTypes.find(rateType => rateType.id === 1)
                      ? 'bg-green-500 border-green-500 text-white'
                      : 'bg-white text-gray-700 border-gray-300'"
                >
                  Fixed
                </button>
              </span>
            </div>
            <div
              v-if="currentScreen === 5"
              :key="`screen-5`"
              class="flex flex-col justify-center"
            >
              <p class="text-center text-gray-500 sm:px-5 mb-5">
                Lenders offer different repayment plans. You can choose to start paying your
                loans immediately or defer until after you graduation. While in-school, you
                can also choose to pay only the interest or a fixed amount (e.g. $25 - $50).
              </p>

              <div class="w-full sm:w-2/3 md:w-1/2 mx-auto mt-10">
                <Multiselect
                  v-model="$selectedRepaymentPlans"
                  track-by="plan"
                  label="plan"
                  placeholder="Select all your preferences"
                  :multiple="true"
                  :disabled="loading"
                  :options="repaymentPlans"
                  :searchable="false"
                  @input="handleFilterChange"
                ></Multiselect>
              </div>
              <button
                class="text-green-400 text-xs mt-10 focus:outline-none
                active:border-none"
                @click="selectAllPaymentPlans()"
              >
                I don't have a preference.
              </button>
            </div>
          </transition>
        </div>
        <OnboardingFooter
          :currentScreen="currentScreen"
          :totalScreens="totalScreens"
          :nextDisabled="disableNext"
          @nextScreen="animationName = 'slide-right-fade'; currentScreen += 1"
          @skip="$emit('skip')"
          @finish="$emit('finish')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import OnboardingFooter from '@/components/onboarding/OnboardingFooter.vue';
import OnboardingHeader from '@/components/onboarding/OnboardingHeader.vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { getHistogramData } from '@/services/api';

export default {
  name: 'Onboarding',
  components: { OnboardingHeader, OnboardingFooter },
  data() {
    return {
      currentScreen: 1,
      totalScreens: 5,
      hasSelectedCosigner: false,
      titles: [
        'What type of loan are you looking for today?',
        'What’s your credit score?',
        'Are you looking for quotes with a co-signer?',
        'Are you interested in variable or fixed rates?',
        'Do you have a peferred repayment plan?',
      ],
      coSignerTitle: 'What’s you co-signer’s credit score?',
      animationName: 'slide-left-fade',
    };
  },
  computed: {
    ...mapGetters('base', [
      'reversedCreditScores',
      'reversedCosignerCreditScores',
    ]),
    ...mapState('base', [
      'loading',
      'showCosignerCreditScores',
      'universities',
      'creditScores',
      'cosignerCreditScores',
      'degrees',
      'rateTypes',
      'repaymentPlans',
      'lenders',
      'selectedLoanTypes',
      'selectedCreditScores',
      'selectedCosignerCreditScores',
      'selectedUniversities',
      'selectedDegrees',
      'selectedRateTypes',
      'selectedRepaymentPlans',
      'selectedLenders',
    ]),
    ...mapState('submission', [
      'submissions',
    ]),
    $selectedCreditScores: {
      get() {
        return this.selectedCreditScores;
      },
      set(val) {
        this.setSelectedValue({ key: 'CreditScores', val });
      },
    },
    $selectedCosignerCreditScores: {
      get() {
        return this.selectedCosignerCreditScores;
      },
      set(val) {
        this.setSelectedValue({ key: 'CosignerCreditScores', val });
      },
    },
    $selectedRepaymentPlans: {
      get() {
        return this.selectedRepaymentPlans;
      },
      set(val) {
        this.setSelectedValue({ key: 'RepaymentPlans', val });
      },
    },
    currentScreenString() {
      return `(${this.currentScreen} of ${this.totalScreens})`;
    },
    currentTitle() {
      if (this.showCosignerCreditScores && this.currentScreen === 3) {
        return 'What’s your co-signer’s credit score?';
      }

      return this.titles[this.currentScreen - 1];
    },
    disableCosignerNext() {
      if (!this.showCosignerCreditScores) {
        return false;
      }
      return this.selectedCosignerCreditScores.length === 0;
    },
    disableNext() {
      const functions = [
        () => this.selectedLoanTypes.length === 0,
        () => this.selectedCreditScores.length === 0,
        () => this.disableCosignerNext,
        () => this.selectedRateTypes.length === 0,
        () => this.selectedRepaymentPlans.length === 0,
      ];
      return functions[this.currentScreen - 1]();
    },
  },
  methods: {
    ...mapActions('base', [
      'setAllOptions',
      'setBins',
      'setSelectedValue',
      'toggleCosigners',
    ]),
    setLoanType(val) {
      if (this.selectedLoanTypes.includes(val)) {
        this.setSelectedValue({ key: 'LoanTypes', val: [] });
      } else {
        this.setSelectedValue({ key: 'LoanTypes', val: [val] });
      }
      this.handleFilterChange();
    },
    setRateType(val) {
      if (this.selectedRateTypes.includes(val)) {
        this.setSelectedValue({ key: 'RateTypes', val: [] });
      } else {
        this.setSelectedValue({ key: 'RateTypes', val: [val] });
      }
      this.handleFilterChange();
    },
    handleFilterChange() {
      getHistogramData({
        hasCosigner: this.showCosignerCreditScores,
        creditScores: this.selectedCreditScores,
        cosignerCreditScores: this.selectedCosignerCreditScores,
        universities: this.selectedUniversities,
        degreeTypes: this.selectedLoanTypes.map((item) => item.toLowerCase()),
        rateTypes: this.selectedRateTypes,
        repaymentPlans: this.selectedRepaymentPlans,
        lenders: this.selectedLenders,
      })
        .then((response) => {
          if (response) {
            this.setBins(response.data);
          }
        });
    },
    selectAllPaymentPlans() {
      this.setSelectedValue({ key: 'RepaymentPlans', val: [...this.repaymentPlans] });
    },
  },
  watch: {
    loading(value) {
      if (value === false) {
        this.selectAllPaymentPlans();
      }
    },
  },
  mounted() {
    if (!this.loading) {
      this.selectAllPaymentPlans();
    }
  },
};
</script>

<style lang="scss">
.onboarding-window {

  .onboarding-window-inside {
    height:0;
    padding-bottom:60%;

    @media (orientation: portrait) {
      padding-bottom:160%;
      @screen sm {
        padding-bottom:100%;
      }
    }
  }
}
</style>
